import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CheckoutRequest,
  CheckoutResponse,
  PortalResponse,
  Subscription,
  SubscriptionPlan,
  SubscriptionPlanInput,
  PaymentMethodCard,
  Ticket,
  PaymentFailure,
} from '@pirouette/shared';
import { checkoutRequestSchema, subscriptionPlanInputSchema } from '@pirouette/shared';
import { supabase } from '@/lib/supabase';
import { stripUndefined } from '@/lib/db';

// ---- Subscription plans ----------------------------------------------------

export function useSubscriptionPlans(opts?: { activeOnly?: boolean }) {
  const activeOnly = opts?.activeOnly ?? false;
  return useQuery({
    queryKey: ['subscription-plans', { activeOnly }],
    queryFn: async (): Promise<SubscriptionPlan[]> => {
      let q = supabase.from('subscription_plans').select('*').order('monthly_cents');
      if (activeOnly) q = q.eq('active', true);
      const { data, error } = await q;
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useCreateSubscriptionPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SubscriptionPlanInput) => {
      const parsed = subscriptionPlanInputSchema.parse(input);
      const { error } = await supabase.from('subscription_plans').insert(stripUndefined(parsed));
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['subscription-plans'] });
    },
  });
}

export function useToggleSubscriptionPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; active: boolean }) => {
      const { error } = await supabase
        .from('subscription_plans')
        .update({ active: args.active })
        .eq('id', args.id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['subscription-plans'] });
    },
  });
}

// ---- Subscriptions --------------------------------------------------------

export function useFamilySubscriptions(familyId: string | undefined) {
  return useQuery({
    enabled: !!familyId,
    queryKey: ['subscriptions', familyId],
    queryFn: async (): Promise<Subscription[]> => {
      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('family_id', familyId!)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useAllSubscriptions() {
  return useQuery({
    queryKey: ['subscriptions'],
    queryFn: async (): Promise<Subscription[]> => {
      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });
}

// ---- Payment methods ------------------------------------------------------

export function useFamilyPaymentMethods(familyId: string | undefined) {
  return useQuery({
    enabled: !!familyId,
    queryKey: ['payment-methods', familyId],
    queryFn: async (): Promise<PaymentMethodCard[]> => {
      const { data, error } = await supabase
        .from('payment_methods')
        .select('*')
        .eq('family_id', familyId!)
        .order('is_default', { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });
}

// ---- Tickets --------------------------------------------------------------

export function useFamilyTickets(familyId: string | undefined) {
  return useQuery({
    enabled: !!familyId,
    queryKey: ['tickets', 'family', familyId],
    queryFn: async (): Promise<Ticket[]> => {
      const { data, error } = await supabase
        .from('tickets')
        .select('*')
        .eq('family_id', familyId!)
        .order('purchased_at', { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useShowTickets(showId: string | undefined) {
  return useQuery({
    enabled: !!showId,
    queryKey: ['tickets', 'show', showId],
    queryFn: async (): Promise<Ticket[]> => {
      const { data, error } = await supabase.from('tickets').select('*').eq('show_id', showId!);
      if (error) throw error;
      return data ?? [];
    },
  });
}

// ---- Checkout via Edge Function -------------------------------------------

export function useCreateCheckoutSession() {
  return useMutation({
    mutationFn: async (input: CheckoutRequest): Promise<CheckoutResponse> => {
      const parsed = checkoutRequestSchema.parse(input);
      const res = await supabase.functions.invoke<CheckoutResponse | { error: string }>(
        'stripe-checkout-create',
        { body: parsed },
      );
      if (res.error) throw res.error;
      const resp = res.data!;
      if ('error' in resp) throw new Error(resp.error);
      return resp;
    },
  });
}

export function useOpenCustomerPortal() {
  return useMutation({
    mutationFn: async (args: { return_url: string }): Promise<PortalResponse> => {
      const res = await supabase.functions.invoke<PortalResponse | { error: string }>(
        'stripe-portal-create',
        { body: args },
      );
      if (res.error) throw res.error;
      const resp = res.data!;
      if ('error' in resp) throw new Error(resp.error);
      return resp;
    },
  });
}

// ---- Seat holds for the interactive seat map ------------------------------

export function useCreateTicketHold() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      seat_id: string;
      show_id: string;
      hold_minutes: number;
    }): Promise<string> => {
      const userResp = await supabase.auth.getUser();
      const userId = userResp.data.user?.id;
      if (!userId) throw new Error('not signed in');
      const expiresAt = new Date(Date.now() + args.hold_minutes * 60_000).toISOString();
      const { data, error } = await supabase
        .from('ticket_holds')
        .insert({
          seat_id: args.seat_id,
          show_id: args.show_id,
          held_by_user_id: userId,
          expires_at: expiresAt,
        })
        .select('id')
        .single();
      if (error) throw error;
      return data.id;
    },
    onSuccess: (_, args) => {
      void qc.invalidateQueries({ queryKey: ['seat-availability', args.show_id] });
    },
  });
}

// Sprint 11: auto-pick best-available contiguous seats.
export interface AutoHoldResult {
  hold_id: string;
  seat_id: string;
}

export function useAutoHoldSeats() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      show_id: string;
      quantity: number;
      hold_minutes: number;
    }): Promise<AutoHoldResult[]> => {
      const { data, error } = await supabase.rpc('auto_hold_seats', {
        p_show_id: args.show_id,
        p_quantity: args.quantity,
        p_hold_minutes: args.hold_minutes,
      });
      if (error) throw error;
      return data ?? [];
    },
    onSuccess: (_, args) => {
      void qc.invalidateQueries({ queryKey: ['seat-availability', args.show_id] });
    },
  });
}

export interface ShowSaleStatus {
  show_id: string;
  production_id: string;
  name: string;
  performance_at: string;
  max_tickets_per_purchase: number | null;
  ticket_sales_open: boolean;
  ticket_sales_open_at: string | null;
  on_sale: boolean;
  seats_available: number;
}

export function useShowSaleStatus(showId: string | undefined) {
  return useQuery({
    enabled: !!showId,
    queryKey: ['show-sale-status', showId],
    // The available-seat count changes every time another shopper holds
    // a seat. Refresh more frequently than the seat-map view.
    refetchInterval: 15_000,
    queryFn: async (): Promise<ShowSaleStatus | null> => {
      const { data, error } = await supabase
        .from('show_sales_status')
        .select('*')
        .eq('show_id', showId!)
        .maybeSingle();
      if (error) throw error;
      return (data as ShowSaleStatus | null) ?? null;
    },
  });
}

export function useReleaseTicketHold() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { hold_id: string; show_id: string }) => {
      // HIGH-6: the underlying RLS policy filters on held_by_user_id so a
      // different user's hold id silently affects 0 rows. Use `count:
      // 'exact'` to detect the silent-failure case and surface it as an
      // error so the UI doesn't lie to the user.
      const userResp = await supabase.auth.getUser();
      const userId = userResp.data.user?.id;
      const { count, error } = await supabase
        .from('ticket_holds')
        .update({ released: true }, { count: 'exact' })
        .eq('id', args.hold_id)
        .eq('held_by_user_id', userId ?? '');
      if (error) throw error;
      if (count === 0) throw new Error('hold not found or not owned by you');
    },
    onSuccess: (_, args) => {
      void qc.invalidateQueries({ queryKey: ['seat-availability', args.show_id] });
    },
  });
}

/**
 * Per-show map of seats that are either sold (tickets) or currently held
 * (ticket_holds with future expiry). The seat-map renderer paints these
 * unselectable.
 */
export interface SeatAvailability {
  sold_seat_ids: string[];
  held_seat_ids: string[];
}

export function useSeatAvailability(showId: string | undefined) {
  return useQuery({
    enabled: !!showId,
    queryKey: ['seat-availability', showId],
    queryFn: async (): Promise<SeatAvailability> => {
      const [ticketsResp, holdsResp] = await Promise.all([
        supabase.from('tickets').select('seat_id').eq('show_id', showId!).eq('status', 'sold'),
        supabase
          .from('ticket_holds')
          .select('seat_id, expires_at, released')
          .eq('show_id', showId!)
          .eq('released', false),
      ]);
      if (ticketsResp.error) throw ticketsResp.error;
      if (holdsResp.error) throw holdsResp.error;
      const now = Date.now();
      return {
        sold_seat_ids: (ticketsResp.data ?? []).map((r: { seat_id: string }) => r.seat_id),
        held_seat_ids: (holdsResp.data ?? [])
          .filter(
            (r: { expires_at: string; released: boolean }) =>
              !r.released && new Date(r.expires_at).getTime() > now,
          )
          .map((r: { seat_id: string }) => r.seat_id),
      };
    },
    // The held set changes as other shoppers click — refresh every 15s so
    // we don't let two people Checkout the same seat.
    refetchInterval: 15_000,
  });
}

// ---- Admin: payment failures and checkout sessions ------------------------

export function useFamilyPaymentFailures(familyId: string | undefined) {
  return useQuery({
    enabled: !!familyId,
    queryKey: ['payment-failures', familyId],
    queryFn: async (): Promise<PaymentFailure[]> => {
      const { data, error } = await supabase
        .from('payment_failures')
        .select('*')
        .eq('family_id', familyId!)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useResolvePaymentFailure() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; family_id: string }) => {
      const { error } = await supabase
        .from('payment_failures')
        .update({ resolved_at: new Date().toISOString() })
        .eq('id', args.id);
      if (error) throw error;
    },
    onSuccess: (_, args) => {
      void qc.invalidateQueries({
        queryKey: ['payment-failures', args.family_id],
      });
    },
  });
}
